/**
 * «Date libere» della BI (Davide 03/11/2026: «quando clicco date libere si
 * rompe tutto»).
 *
 * Il campo data del browser cambia valore a ogni tasto: scrivendo 2026 passa
 * da 0002, 0020, 0202. Prima ogni passaggio partiva come richiesta al server
 * (anni assurdi, conti su tutta la storia, errori). Qui le date restano una
 * bozza finche' non sono entrambe vere: anno dal 2020, non nel futuro, «dal»
 * non dopo «al». Solo allora, dopo una breve pausa, la BI si aggiorna.
 */

import { useEffect, useState } from "react";

// Davide 04/11/2026: «non deve andare a capo quando viene cliccato, devono
// essere sempre fissi i campi». I due campi ci sono sempre e mostrano il
// periodo scelto; larghezza fissa, e l'avviso sta sotto, sopra la pagina, senza
// spingere niente. Cambiare una data passa a «Date libere».

const PAUSA_MS = 600;
const PRIMO_ANNO = 2020;
const FORMA = /^\d{4}-\d{2}-\d{2}$/;

export function dataValida(v: string, oggi: string): boolean {
  return FORMA.test(v) && Number(v.slice(0, 4)) >= PRIMO_ANNO && v <= oggi;
}

/** Il problema delle due date, in parole, o ``null`` se vanno bene. */
export function problemaDate(dal: string, al: string, oggi: string): string | null {
  if (!dal || !al) return "Scegli tutte e due le date.";
  if (!dataValida(dal, oggi) || !dataValida(al, oggi)) return `Date dal ${PRIMO_ANNO} a oggi.`;
  if (dal > al) return "«Dal» viene dopo «al».";
  return null;
}

const CAMPO =
  "h-[34px] w-[140px] shrink-0 rounded-full border bg-card px-3 text-[13px] font-semibold tabular-nums text-ink transition-[border-color] hover:border-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 max-md:h-11 max-md:w-auto max-md:min-w-0 max-md:flex-1 max-md:text-[15px]";

export function DateLibere({
  dal,
  al,
  oggi,
  onChange,
}: {
  dal: string;
  al: string;
  oggi: string;
  onChange: (dal: string, al: string) => void;
}) {
  const [bozzaDal, setBozzaDal] = useState(dal);
  const [bozzaAl, setBozzaAl] = useState(al);
  // Un periodo nuovo dalle pillole riscrive i campi.
  useEffect(() => {
    setBozzaDal(dal);
    setBozzaAl(al);
  }, [dal, al]);
  const toccate = bozzaDal !== dal || bozzaAl !== al;
  // «Tutto» non ha un inizio: il campo vuoto non e' un errore finche' non lo tocchi.
  const problema = toccate ? problemaDate(bozzaDal, bozzaAl, oggi) : null;

  useEffect(() => {
    if (problema || !toccate) return;
    const t = window.setTimeout(() => onChange(bozzaDal, bozzaAl), PAUSA_MS);
    return () => window.clearTimeout(t);
  }, [bozzaDal, bozzaAl, problema, toccate, onChange]);

  const bordo = problema ? "border-red" : "border-line-2";
  return (
    <div className="relative flex shrink-0 flex-nowrap items-center gap-2 max-md:w-full">
      <label className="flex items-center gap-1.5 text-[13px] font-semibold max-md:min-w-0 max-md:flex-1">
        Dal
        <input
          type="date"
          aria-label="Dal giorno"
          aria-invalid={problema ? true : undefined}
          value={bozzaDal}
          min={`${PRIMO_ANNO}-01-01`}
          max={bozzaAl || oggi}
          onChange={(e) => setBozzaDal(e.target.value)}
          className={`${CAMPO} ${bordo}`}
        />
      </label>
      <label className="flex items-center gap-1.5 text-[13px] font-semibold max-md:min-w-0 max-md:flex-1">
        al
        <input
          type="date"
          aria-label="Al giorno"
          aria-invalid={problema ? true : undefined}
          value={bozzaAl}
          min={bozzaDal || `${PRIMO_ANNO}-01-01`}
          max={oggi}
          onChange={(e) => setBozzaAl(e.target.value)}
          className={`${CAMPO} ${bordo}`}
        />
      </label>
      {problema && (
        <span
          role="status"
          className="absolute right-0 top-full z-10 mt-1.5 whitespace-nowrap rounded-[8px] bg-red px-2.5 py-1 text-[12px] font-semibold text-white shadow-md"
        >
          {problema}
        </span>
      )}
    </div>
  );
}
